import { useState, useEffect, type ElementType } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { ModuleProperties } from '@/lib/core/types';
import { HomePageHighlightsForYouElementModel, EventListItemDto } from '@/services/platform/api/model';
import {
  getUserPreferences,
  getPreferencesCategories,
  getPreferencesSubcategories,
} from '@/services/platform/data/preferences';
import { getEvents, toDateKey } from '@/services/platform/data/events';
import { useLocaleStore } from '@/lib/core/stores';
import { useApp } from '@/lib/core/providers/appProvider';
import { useClientTranslation } from '@/lib/core/i18n';
import { ImagesToImageMap } from '@/lib/core/utils';
import { cn } from '@/lib/core/utils/cn';
import { Heading } from '@/components/ui/heading';
import ModuleWrapper from '@/components/shared/module/wrapper';
import { useEventCategoryLabel } from '@/components/shared/events/list/categories';

/** Max number of interest-matched events surfaced on the home page. */
const MAX_HIGHLIGHTS = 6;

/** Pool size fetched when a client-side weekday filter still needs to narrow the results. */
const WEEKDAY_FILTER_POOL_SIZE = 100;

/** `preferenceCategory.externalId` identifying the Events interests category — stable across locales. */
const EVENTS_PREFERENCE_CATEGORY_EXTERNAL_ID = 'events';

/** `preferenceSubcategory.externalId` values under the Events category. */
const EVENT_CATEGORY_SUBCATEGORY_EXTERNAL_ID = 'category';
const WEEK_DAY_SUBCATEGORY_EXTERNAL_ID = 'week_day';

/** Lowercase English weekday name (e.g. "monday") for a date key, matching the CMS `preference.externalId` values. */
function weekdayFromDateKey(dateKey: string): string {
  const date = new Date(`${dateKey}T00:00:00Z`);
  return new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: 'UTC' }).format(date).toLowerCase();
}

// ── MiniCard ─────────────────────────────────────────────────────────────────

function MiniCard({ event, onClick }: { event: EventListItemDto; onClick: () => void }) {
  const t = useClientTranslation();
  const getCategoryLabel = useEventCategoryLabel();
  const { appSettings } = useApp();

  // Fall back to the CMS-configured no-pic image if the event has none or it fails to load;
  // the `bg-[#2a2a2a]` on the wrapper is the last resort when even that setting is unconfigured.
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [event.imageUrl]);
  const showImage = !!event.imageUrl && !imageFailed;
  const fallbackImageUrl = ImagesToImageMap(appSettings?.properties?.eventNoPicImage)?.src;

  return (
    <article
      className="flex w-[244px] shrink-0 cursor-pointer flex-col gap-2"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
    >
      <div className="relative h-[320px] w-full overflow-hidden bg-[#2a2a2a]">
        {showImage ? (
          <img
            src={event.imageUrl ?? undefined}
            alt={event.title ?? ''}
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        ) : (
          fallbackImageUrl && (
            <img
              src={fallbackImageUrl}
              alt={event.title ?? ''}
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
          )
        )}
      </div>

      <div className="flex flex-col gap-2 text-white">
        <div className="flex flex-col tracking-[0.5px] uppercase">
          {event.eventCategory && (
            <span className="font-pp-neue-montreal text-[12px] font-normal">
              {getCategoryLabel(event.eventCategory)}
            </span>
          )}
          <span className="font-pp-neue-montreal text-[14px] font-semibold">{event.title ?? '—'}</span>
        </div>
        <span className="font-pp-neue-montreal text-[16px] font-normal tracking-[0.25px]">
          {t('App.Events.ForYourNextVisit')}
        </span>
      </div>
    </article>
  );
}

// ── HomePageHighlightsForYou ──────────────────────────────────────────────────

export const HomePageHighlightsForYou = (props: ModuleProperties) => {
  const content = props.content as HomePageHighlightsForYouElementModel | undefined;
  const properties = content?.properties;
  const title = properties?.title ?? 'Highlights for you';
  const headingAs = (properties?.titleHeading?.toLowerCase() ?? 'h2') as ElementType;
  const navigate = useNavigate();
  const locale = useLocaleStore(state => state.locale);
  const { appSettings } = useApp();

  const [highlights, setHighlights] = useState<EventListItemDto[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!appSettings) return;
    let cancelled = false;

    Promise.all([getPreferencesCategories(appSettings, locale), getUserPreferences(locale)])
      .then(([categories, preferences]) => {
        const eventsCategory = categories?.find(
          c => c.properties?.externalId === EVENTS_PREFERENCE_CATEGORY_EXTERNAL_ID
        );
        if (!eventsCategory) return [];

        return getPreferencesSubcategories(eventsCategory.id, locale).then(subcategories => {
          const externalIdByName = new Map(
            (subcategories ?? []).map(s => [s.name, s.properties?.externalId])
          );

          const eventsPreferences = (preferences ?? []).filter(p => p.categoryNodeKey === eventsCategory.id);

          const eventCategoryValues = eventsPreferences
            .filter(p => externalIdByName.get(p.subcategory ?? '') === EVENT_CATEGORY_SUBCATEGORY_EXTERNAL_ID)
            .map(p => p.value)
            .filter((v): v is string => !!v);

          const weekdayValues = eventsPreferences
            .filter(p => externalIdByName.get(p.subcategory ?? '') === WEEK_DAY_SUBCATEGORY_EXTERNAL_ID)
            .map(p => p.value)
            .filter((v): v is string => !!v);

          if (eventCategoryValues.length === 0 && weekdayValues.length === 0) return [];

          // A weekday filter narrows client-side (no server param for individual weekdays),
          // so fetch a larger pool before slicing down to MAX_HIGHLIGHTS.
          const pageSize = weekdayValues.length > 0 ? WEEKDAY_FILTER_POOL_SIZE : MAX_HIGHLIGHTS;
          const eventCategory = eventCategoryValues.length > 0 ? eventCategoryValues : undefined;

          // excludeExternal: true — external events (ticketed on partner sites) show only in the
          // calendar, not in the CMS-driven highlights module.
          return getEvents({ eventCategory, pageSize, excludeExternal: true }, locale).then(result => {
            const items = result?.items ?? [];
            if (weekdayValues.length === 0) return items;
            return items.filter(event => {
              const dateKey = toDateKey(event.startDateTime);
              return !!dateKey && weekdayValues.includes(weekdayFromDateKey(dateKey));
            });
          });
        });
      })
      .then(events => {
        if (!cancelled) setHighlights(events.slice(0, MAX_HIGHLIGHTS));
      })
      .catch(() => {
        if (!cancelled) setHighlights([]);
      });

    return () => {
      cancelled = true;
    };
  }, [appSettings, locale]);

  if (highlights.length === 0) return null;

  return (
    <ModuleWrapper
      component={props}
      isFullWidth={true}
      classNames={{
        base: 'module__highlights-for-you',
        wrapper: 'py-0 lg:py-0',
        grid: 'grid-cols-1 lg:grid-cols-1 gap-0',
      }}
    >
      <div className="col-span-1 flex flex-col gap-6 px-4 py-6">
        <Heading as={headingAs} className="font-pp-neue-montreal text-[24px] font-semibold text-white">
          {title}
        </Heading>

        {highlights.length > 0 && (
          <>
            <div className="w-[calc(100%+16px)] overflow-hidden" aria-label={title}>
              <Swiper
                slidesPerView="auto"
                spaceBetween={8}
                onActiveIndexChange={swiper => setActiveIndex(swiper.activeIndex)}
                onReachEnd={() => setActiveIndex(highlights.length - 1)}
              >
                {highlights.map(event => {
                  const eventDetailPath = appSettings?.properties?.eventDetail?.route?.path;
                  const url = event.id && eventDetailPath ? `${eventDetailPath}/${event.id}` : '/';
                  return (
                    <SwiperSlide key={event.id} style={{ width: 'auto' }}>
                      <MiniCard event={event} onClick={() => navigate(url)} />
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </div>

            {highlights.length > 1 && (
              <div className="flex items-center justify-center gap-2 px-4" aria-hidden="true">
                {highlights.map((_, i) => (
                  <span
                    key={i}
                    className={cn(
                      'block h-px transition-all duration-300',
                      i === activeIndex ? 'w-4 bg-white' : 'w-4 bg-white/30'
                    )}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </ModuleWrapper>
  );
};
